/**
 * BrandEntityView - Displays AgentPlat entity information for a brand
 */
import { useEffect, useState, useCallback } from 'react'
import { CompanyCard, Button, type AgentPlatEntity } from '@trafiflow/shared-ui'
import apiClient from '@/shared/lib/api'
import { ArrowLeft } from 'lucide-react'
import type { Brand } from '../types'

interface BrandEntityViewProps {
  brand: Brand
  onBack: () => void
}

export default function BrandEntityView({ brand, onBack }: BrandEntityViewProps) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [entity, setEntity] = useState<AgentPlatEntity | null>(null)

  const fetchEntity = useCallback(async () => {
    if (!brand.url) {
      setError('This brand does not have a URL')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get(`/brands/${brand.id}/entity`)
      setEntity(response.data.entity as AgentPlatEntity)
    } catch (err: unknown) {
      console.error('Error fetching entity:', err)
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      const errorMessage = error.response?.data?.message || 'Failed to load entity information'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [brand.id, brand.url])

  useEffect(() => {
    fetchEntity()
  }, [fetchEntity])

  return (
    <div className="space-y-4">
      {/* Header with back button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            onClick={onBack}
            icon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Brands
          </Button>
          <div>
            <h2 className="text-xl font-semibold text-tf-text">Company Information</h2>
            <p className="text-sm text-tf-text-muted mt-1">
              Company data for: <span className="font-medium">{brand.name}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Entity Card */}
      <CompanyCard
        entity={entity || undefined}
        loading={loading}
        error={error}
        showMetadata={true}
        className="w-full"
      />
    </div>
  )
}
